import { rememberPendingSignOut } from "./pending-sign-out";
import type { MeResponse, Role } from "@starsach/shared-types";
import type { AppConfig } from "../config";
import { cognitoApi, cognitoServiceRoot } from "./cognito-api";
import type { HostedSignIn } from "./hosted-sign-in";
import { adoptTabEntry, clearTokens, expiryOf, markSigningOut, onSessionChange, readTokens, signingOutElsewhere, withRenewLock, writeTokens, type StoredTokens } from "./session-store";
import type { SignedIn } from "./sign-in-machine";

/**
 * Starsach's session layer (crmarr/templates auth-self-hosted-sign-in.md, R3 and R4), ported from gairm/lms 280619c
 * (portal/src/auth/auth.ts). The tokens live in Starsach's own store (session-store.ts: localStorage, one session per
 * browser shared by every tab, Chris 2026-10-02). A native person signs in on Starsach's own screen (pages/LoginPage.tsx
 * and sign-in-machine.ts) and the machine hands the tokens and the confirmed device to `acceptSignIn`. Renewal goes
 * through the Cognito API (`GetTokensFromRefreshToken`) with the confirmed device's key. Only federation goes through
 * the hosted endpoint (R7), behind hosted-sign-in.ts; first sign-in and Forgot password are Starsach's own screens.
 */

export interface Session {
  idToken: string;
  /** Carries the aws.cognito.signin.user.admin scope: what the browser manages its own user with (section 13.3). */
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  /** Cognito's username claim - what USER_AUTH is started with for a step-up. */
  username?: string;
  /** When the user last proved who they are (id token `auth_time`, epoch seconds); step-up compares against it. */
  authTime?: number;
  /**
   * When this sign-in happened (epoch ms), set once at sign-in and kept across step-ups - which renew `auth_time` but
   * are not a sign-in from the user's side. "Do later" on a nudge means "until the next of these".
   */
  signedInAt?: number;
  expiresAt: number;
}

/** Fifteen minutes (the owner, 2026-10-01): the fallback when a token carries no `exp`. */
const TOKEN_SECONDS = 15 * 60;

/** The sign-in id ("Do later" on a nudge): one per sign-in, shared by the browser's tabs since the session is. */
const SIGNED_IN_AT_KEY = "starsach.signedInAt";
function signedInAtOf(): number | undefined {
  try {
    const n = Number(window.localStorage.getItem(SIGNED_IN_AT_KEY) ?? NaN);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
}
export function markSignedIn(): void {
  try {
    window.localStorage.setItem(SIGNED_IN_AT_KEY, String(Date.now()));
  } catch {
    // No storage: every load is a new sign-in to the nudges.
  }
}
function forgetSignedIn(): void {
  try {
    window.localStorage.removeItem(SIGNED_IN_AT_KEY);
    window.sessionStorage.removeItem(SIGNED_IN_AT_KEY);
  } catch {
    // Nothing was kept.
  }
}

/** The tokens a fresh in-app authentication (step-up) or a renewal hands back. */
export interface FreshTokens {
  IdToken?: string;
  AccessToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
  /** The device Cognito issued to this API sign-in (the pool tracks devices); unconfirmed until ConfirmDevice. */
  NewDeviceMetadata?: { DeviceKey?: string; DeviceGroupKey?: string };
}

/**
 * Where the hosted endpoint is asked to go (ADR-0014, R7): only federated sign-ins leave for it. `idpIdentifier`: an
 * email domain registered on a provider (the sign-in screen's email-first routing); `identityProvider`: the provider's
 * name (a federated step-up). `loginHint`: the address typed, so the provider need not ask again. `stepUp`: a step-up
 * through the provider, which asks for a real sign-in (`prompt=login`).
 */
export interface SignInRoute {
  idpIdentifier?: string;
  identityProvider?: string;
  loginHint?: string;
  stepUp?: boolean;
}

/**
 * R7 (gairm/lms#398): a federated session was signed out in this browser. The next sign-in through a provider is sent
 * with `prompt=login`, asking for a real sign-in rather than an answer from a session cookie; kept until a federated
 * sign-in comes back. Whether the organization's own identity provider then asks again is its business - the residual
 * Account > Security states.
 */
const REAUTH_KEY = "starsach.ssoReauth";
function noteFederatedSignOut(): void {
  try {
    window.localStorage.setItem(REAUTH_KEY, "1");
  } catch {
    // No storage: the next federated sign-in may be answered by the hosted endpoint's cookie.
  }
}
function reauthAsked(): boolean {
  try {
    return window.localStorage.getItem(REAUTH_KEY) === "1";
  } catch {
    return false;
  }
}
function clearReauth(): void {
  try {
    window.localStorage.removeItem(REAUTH_KEY);
  } catch {
    // As above.
  }
}

/** The hosted endpoint's query for a route (R7): the provider, the login hint, and prompt=login on a step-up or after a federated sign-out. */
export function providerQuery(route: SignInRoute): Record<string, string> {
  return {
    ...(route.idpIdentifier ? { idp_identifier: route.idpIdentifier } : route.identityProvider ? { identity_provider: route.identityProvider } : {}),
    ...(route.loginHint ? { login_hint: route.loginHint } : {}),
    ...(route.stepUp || reauthAsked() ? { prompt: "login" } : {}),
  };
}

/** Cognito's `identities` claim on an ID token - the linked providers it writes for every federated sign-in - if any. */
function identitiesOf(idToken: string | undefined): unknown[] | undefined {
  const identities = claimsOf(idToken ?? "")["identities"];
  return Array.isArray(identities) ? identities : undefined;
}

/** Whether an ID token is a federated sign-in's: the sign-out's /logout, the step-up's provider. */
export function isFederated(idToken: string | undefined): boolean {
  return identitiesOf(idToken) !== undefined;
}

/** The provider a federated ID token names (its first `identities` entry), if it names one. */
export function federatedProviderOf(idToken: string | undefined): string | undefined {
  const name = (identitiesOf(idToken)?.[0] as { providerName?: unknown } | undefined)?.providerName;
  return typeof name === "string" && name ? name : undefined;
}

export interface AuthAdapter {
  getSession(): Promise<Session | null>;
  /** Renews now with the refresh token; null when it cannot, in which case the store is cleared. */
  renew(): Promise<Session | null>;
  /** Removes the session without leaving the page. */
  forget(): Promise<void>;
  /** After a step-up: the new ID and access tokens (and auth_time); the session keeps its own refresh token. */
  replaceTokens(tokens: FreshTokens): Promise<Session | null>;
  /** A sign-in on Starsach's own screen has finished: its tokens and the confirmed device become the session. */
  acceptSignIn(result: SignedIn): Promise<Session | null>;
  /** Leaves for the hosted endpoint: federation only (R7). */
  signIn(returnTo: string, route: SignInRoute): Promise<void>;
  /** Back from the hosted endpoint at /auth/callback: the code exchange; returns where to land. */
  completeSignIn(): Promise<string>;
  /** Ends this sign-in (R4): stamp, forget the device unless remembered, revoke, clear - then Starsach's sign-in, or the hosted logout for a federated session. */
  signOut(opts?: { alreadyRecorded?: boolean }): Promise<void>;
  /** The session changed underneath the app: renewed, or gone. `elsewhere` when another tab of this browser did it. */
  onSessionChange(listener: (session: Session | null, elsewhere?: boolean) => void): () => void;
}

/**
 * Before the shared store, a view-as tab borrowed the opener's per-tab session through a hand-off in localStorage
 * (#593). Every tab now shares the browser's session, so the hand-off and the tokens it held go.
 */
const OLD_HANDOFF_KEY = "starsach-view-as-handoff";

export function cognitoAuth(config: AppConfig): AuthAdapter {
  const origin = window.location.origin;
  const { cognito } = config;
  let hosted: Promise<HostedSignIn> | undefined;
  const hostedEndpoint = () => (hosted ??= import("./hosted-sign-in").then((m) => m.hostedSignIn({ issuer: cognito.issuer, clientId: cognito.clientId, origin })));
  const api = cognitoApi(config);
  try {
    window.localStorage.removeItem(OLD_HANDOFF_KEY);
  } catch {
    // No storage, nothing to remove.
  }

  const listeners = new Set<(session: Session | null, elsewhere?: boolean) => void>();
  const emit = (s: Session | null, elsewhere = false) => {
    for (const l of listeners) l(s, elsewhere);
  };
  let stopWatching: (() => void) | undefined;
  const watchOtherTabs = () =>
    (stopWatching ??= onSessionChange((t) => {
      if (!t) forgetSignedIn();
      emit(toSession(t), true);
    }));

  const toSession = (t: StoredTokens | null): Session | null => {
    if (!t || t.expiresAt <= Date.now()) return null;
    const claims = claimsOf(t.idToken);
    const authTime = authTimeOf(claims);
    if (signedInAtOf() === undefined) markSignedIn();
    return {
      idToken: t.idToken,
      accessToken: t.accessToken,
      ...(t.refreshToken ? { refreshToken: t.refreshToken } : {}),
      ...(typeof claims["email"] === "string" ? { email: claims["email"] } : {}),
      ...(typeof claims["cognito:username"] === "string" ? { username: claims["cognito:username"] } : {}),
      ...(authTime !== undefined ? { authTime } : {}),
      ...(signedInAtOf() !== undefined ? { signedInAt: signedInAtOf()! } : {}),
      expiresAt: t.expiresAt,
    };
  };
  // A tab signed in before the shared store held its own entry: adopted once, here, and removed either way - never on a
  // later read, or a tab whose browser was signed out elsewhere would revive the session from it (mermera#1195).
  adoptTabEntry();
  const stored = (): StoredTokens | null => readTokens();
  const drop = () => {
    clearTokens();
    forgetSignedIn();
    emit(null);
  };

  // One renewal at a time, in this tab and across the browser's tabs (the session is shared): the renewal runs under
  // the browser-wide lock, and a tab that waited on it uses the tokens another tab renewed rather than renewing again.
  let refreshing: Promise<Session | null> | undefined;
  const refresh = (): Promise<Session | null> => {
    const asked = stored()?.idToken;
    return (refreshing ??= withRenewLock(async () => {
      if (isSigningOut()) return null;
      const before = stored();
      if (before && before.idToken !== asked && before.expiresAt - Date.now() > FRESH_ENOUGH_MS) {
        const s = toSession(before);
        emit(s);
        return s;
      }
      if (!before?.refreshToken) {
        if (before) drop();
        return null;
      }
      try {
        // The device key goes with every renewal whose refresh token is bound to a confirmed device; a managed-login
        // (federated, or the interim hosted) refresh token has none.
        const r = await api.getTokensFromRefreshToken(before.refreshToken, before.device?.confirmed ? before.device.key : undefined, RENEW_TIMEOUT_MS);
        const t = r.AuthenticationResult;
        if (!t?.IdToken || !t.AccessToken) throw new Error("Cognito answered without tokens");
        if (isSigningOut()) return null;
        const next: StoredTokens = {
          ...before,
          idToken: t.IdToken,
          accessToken: t.AccessToken,
          refreshToken: t.RefreshToken ?? before.refreshToken,
          expiresAt: expiryOf(t.IdToken, t.ExpiresIn ?? TOKEN_SECONDS),
        };
        writeTokens(next);
        const s = toSession(next);
        emit(s);
        return s;
      } catch {
        drop();
        return null;
      }
    }).finally(() => {
      refreshing = undefined;
    }));
  };

  let departing: Promise<void> | undefined;

  return {
    getSession: async () => {
      const t = stored();
      if (!t) return null;
      if (t.expiresAt > Date.now()) return toSession(t);
      if (!t.refreshToken) {
        drop();
        return null;
      }
      return refresh();
    },
    renew: async () => {
      const t = stored();
      if (!t?.refreshToken) {
        if (t) drop();
        return null;
      }
      return refresh();
    },
    forget: async () => drop(),
    // A step-up's fresh tokens carry the new auth_time. The refresh token stays the session's own: the step-up is an API
    // sign-in, and on a pool that tracks devices its refresh token is bound to a device nobody confirmed, so it can never
    // renew (#607: storing it signed the person out at the next renewal). It is never revoked either, because that would
    // end the access token the security change is about to use.
    replaceTokens: async (t) => {
      const before = stored();
      if (!t.IdToken || !t.AccessToken) return toSession(before);
      const refreshToken = before?.refreshToken ?? (t.NewDeviceMetadata?.DeviceKey ? undefined : t.RefreshToken);
      const next: StoredTokens = {
        ...(before ?? {}),
        idToken: t.IdToken,
        accessToken: t.AccessToken,
        ...(refreshToken ? { refreshToken } : {}),
        expiresAt: expiryOf(t.IdToken, t.ExpiresIn ?? TOKEN_SECONDS),
      };
      writeTokens(next);
      const s = toSession(next);
      emit(s);
      return s;
    },
    acceptSignIn: async ({ tokens: t, device }) => {
      if (isSigningOut() || !t.IdToken || !t.AccessToken) return null;
      const next: StoredTokens = {
        idToken: t.IdToken,
        accessToken: t.AccessToken,
        // An API sign-in's refresh token is bound to its device: kept only with the confirmed device it renews with.
        ...(t.RefreshToken && (device?.confirmed || !t.NewDeviceMetadata) ? { refreshToken: t.RefreshToken } : {}),
        expiresAt: expiryOf(t.IdToken, t.ExpiresIn ?? TOKEN_SECONDS),
        ...(device ? { device } : {}),
      };
      writeTokens(next);
      markSignedIn();
      const s = toSession(next);
      emit(s);
      return s;
    },
    // One departure at a time: a double-click must not start a second redirect, which aborts the first.
    signIn: (returnTo, route) =>
      (departing ??= (async () => {
        if (isSigningOut()) return;
        await (await hostedEndpoint()).redirect({ returnTo, ...(route.stepUp ? { stepUp: true } : {}) }, providerQuery(route));
      })().finally(() => {
        departing = undefined;
      })),
    completeSignIn: async () => {
      const endpoint = await hostedEndpoint();
      const u = await endpoint.callback();
      const state = u.state as { returnTo?: string };
      if (u.idToken && u.accessToken) {
        // The hosted endpoint's tokens, into Starsach's store. It issues no device, so its refresh token renews with no key.
        writeTokens({ idToken: u.idToken, accessToken: u.accessToken, ...(u.refreshToken ? { refreshToken: u.refreshToken } : {}), expiresAt: expiryOf(u.idToken, u.expiresIn ?? TOKEN_SECONDS) });
        await endpoint.forget();
        markSignedIn();
        // Signed in again through the provider: the sign-out's prompt=login has done its work (R7).
        clearReauth();
      }
      emit(toSession(readTokens()));
      return state.returnTo ?? "/";
    },
    // R4, in this order: the mark first (no tab renews or starts a sign-in from here); the stamp at the API, unless the
    // caller already wrote it before a global sign-out; this browser's device forgotten while the access token still
    // works, unless the person chose to remember it (R11: a remembered device stays until Account > Security or Sign out
    // everywhere forgets it); the refresh token revoked with the Cognito API's RevokeToken (gairm/lms#395: a native
    // person's sign-out asks the hosted endpoint nothing); everything cleared in a `finally`. Then Starsach's own sign-in:
    // a native person has no hosted session to end. Only a federated session (the ID token's `identities`) still leaves
    // for /logout, which ends the hosted endpoint's cookie. Each network step is bounded and never blocks the sign-out.
    signOut: async (opts) => {
      signingOut = true;
      markSigningOut(true);
      let federated = false;
      try {
        const t = stored();
        federated = isFederated(t?.idToken);
        if (t?.idToken && !opts?.alreadyRecorded) {
          // #621 (Chris, 2 Oct): up to 30 s; past that the sign-out goes ahead, and the stamp - with this time - is sent
          // again at this person's next sign-in in this browser, and the sign-in page says so meanwhile.
          const at = new Date().toISOString();
          const subject = claimsOf(t.idToken)["sub"];
          if (!(await recordSignOut(config.apiBaseUrl, t.idToken)) && typeof subject === "string") rememberPendingSignOut(subject, at);
        }
        if (t?.device?.key && !t.device.remembered && t.accessToken) await api.forgetDevice(t.accessToken, t.device.key, REVOKE_TIMEOUT_MS).catch(() => undefined);
        if (t?.refreshToken) await revokeRefreshToken(cognitoServiceRoot(config), cognito.clientId, t.refreshToken);
      } finally {
        markSigningOut(false);
        clearTokens();
        forgetSignedIn();
        if (hosted) await (await hosted).forget();
      }
      if (!federated) return window.location.assign("/login");
      noteFederatedSignOut();
      const url = new URL(`https://${cognito.domain}/logout`);
      url.searchParams.set("client_id", cognito.clientId);
      url.searchParams.set("logout_uri", `${origin}/`);
      window.location.assign(url.toString());
    },
    onSessionChange: (listener) => {
      listeners.add(listener);
      watchOtherTabs();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          stopWatching?.();
          stopWatching = undefined;
        }
      };
    },
  };
}

/** A sign-out is in flight in this page or another tab of this browser: no renewal, no sign-in (R4). */
let signingOut = false;
export function isSigningOut(): boolean {
  return signingOut || signingOutElsewhere();
}
/** For tests: a page load's fresh start. */
export function resetSigningOutForTests(): void {
  signingOut = false;
  markSigningOut(false);
}
/** A renewal another tab made counts when the token has at least this long left. */
const FRESH_ENOUGH_MS = 90_000;
/** How long sign-out waits on each of the device and the revoke. */
export const REVOKE_TIMEOUT_MS = 3_000;
/** How long sign-out waits on the stamp (Chris, 2 Oct, #621). */
export const STAMP_TIMEOUT_MS = 30_000;
/** How long a renewal waits on Cognito before the session is treated as ended. */
export const RENEW_TIMEOUT_MS = 10_000;

/** POST /v1/me/sign-out (#578): every token issued until now is refused at the API. Bounded; never throws. */
export async function recordSignOut(apiBaseUrl: string, idToken: string, timeoutMs = STAMP_TIMEOUT_MS): Promise<boolean> {
  try {
    const r = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/v1/me/sign-out`, { method: "POST", headers: { authorization: `Bearer ${idToken}`, accept: "application/json" }, signal: AbortSignal.timeout(timeoutMs) });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * Revokes a refresh token with the Cognito API's `RevokeToken` (gairm/lms#395; R4): the same revocation as the hosted
 * endpoint's /oauth2/revoke, asked of the service root every other sign-in call goes to. The client is public, so the
 * client id is the only credential, and the client has token revocation enabled. Bounded; never throws.
 */
export async function revokeRefreshToken(serviceRoot: string, clientId: string, refreshToken: string, timeoutMs = REVOKE_TIMEOUT_MS): Promise<boolean> {
  try {
    const res = await fetch(serviceRoot, {
      method: "POST",
      headers: { "content-type": "application/x-amz-json-1.1", "x-amz-target": "AWSCognitoIdentityProviderService.RevokeToken" },
      body: JSON.stringify({ Token: refreshToken, ClientId: clientId }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Epoch seconds from the `auth_time` claim, whether the issuer wrote it as a number or a string. */
export function authTimeOf(claims: Record<string, unknown>): number | undefined {
  const raw = claims["auth_time"];
  const n = typeof raw === "number" ? raw : typeof raw === "string" && /^\d+$/.test(raw) ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/** The id token's claims, read without verification: the API verifies; the browser only reads what it was given. */
export function claimsOf(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split(".")[1] ?? "";
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * `mockAuth` (CLAUDE.md section 11): a fake session whose role is chosen on the login screen, in localStorage like the
 * real one (one session per browser), so a second tab shares it and a sign-out in one ends both.
 */
export const MOCK_SESSION_KEY = "mock-session";

export function mockAuth(): AuthAdapter {
  const KEY = MOCK_SESSION_KEY;
  const read = (): Session | null => {
    try {
      const raw = window.localStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  };
  return {
    getSession: async () => read(),
    renew: async () => read(),
    forget: async () => window.localStorage.removeItem(KEY),
    replaceTokens: async (t) => {
      const s = read();
      if (!s) return null;
      const next: Session = { ...s, authTime: Math.floor(Date.now() / 1000), ...(t.AccessToken ? { accessToken: t.AccessToken } : {}) };
      window.localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    },
    // The mock sign-in screen runs the real machine against the mock Cognito API (mocks/fixtures.ts cognito); the ID
    // token it hands over names the persona (mockSessionFromToken), and this sets the mock session for it.
    acceptSignIn: async ({ tokens, device }) => {
      if (isSigningOut() || !tokens.IdToken) return null;
      window.localStorage.setItem(KEY, JSON.stringify(mockSessionFromToken(tokens.IdToken)));
      if (device) window.sessionStorage.setItem("mock.device", JSON.stringify(device));
      return read();
    },
    signIn: async (returnTo, route) => {
      if (isSigningOut()) return;
      // No hosted endpoint to go to: remember where the real adapter would have sent the browser, for the e2e.
      window.sessionStorage.setItem("mock-sign-in-route", JSON.stringify({ returnTo, ...route }));
      window.location.assign("/login");
    },
    completeSignIn: async () => "/",
    // As the real adapter (#621): the stamp first, kept for the next sign-in when the mock API does not take it.
    signOut: async (opts) => {
      const s = read();
      if (s && !opts?.alreadyRecorded) {
        const at = new Date().toISOString();
        const ok = await fetch("/api/v1/me/sign-out", { method: "POST", headers: { authorization: `Bearer ${s.idToken}` } }).then((r) => r.ok, () => false);
        if (!ok) {
          const subject = mockMeOf(s.idToken)?.subject;
          if (subject) rememberPendingSignOut(subject, at);
        }
      }
      window.localStorage.removeItem(KEY);
      window.location.assign("/login");
    },
    onSessionChange: (listener) => {
      const on = (e: StorageEvent) => {
        if (e.storageArea !== window.localStorage || (e.key !== KEY && e.key !== null)) return;
        listener(e.newValue ? (JSON.parse(e.newValue) as Session) : null, true);
      };
      window.addEventListener("storage", on);
      return () => window.removeEventListener("storage", on);
    },
  };
}

/** The /v1/me a mock ID token (`mock.<base64 me>`) carries, or undefined for anything else. */
function mockMeOf(idToken: string): { subject?: string } | undefined {
  try {
    return JSON.parse(atob(idToken.slice(5))) as { subject?: string };
  } catch {
    return undefined;
  }
}

/** A mock ID token (`mock.<base64 me>`) as the session the mock adapter keeps. */
function mockSessionFromToken(idToken: string): Session {
  const me = JSON.parse(atob(idToken.slice(5))) as { email?: string };
  return { idToken, accessToken: idToken, ...(me.email ? { email: me.email, username: me.email } : {}), authTime: Math.floor(Date.now() / 1000), signedInAt: Date.now(), expiresAt: Date.now() + 8 * 3_600_000 };
}

/**
 * ADR-0017: which person the mock session is. The default customer is a
 * manager and the default staff member the owner (a superadmin); `viewer`
 * is a customer holding the built-in viewer, `reader` a `gairm-reader`.
 */
export type MockPersona = "viewer" | "reader";

/** The mock "token" for a persona: the mock API reads the role back out of it. */
export function mockIdToken(role: Role, organisationId?: string, environmentIds?: readonly string[], identityProvider?: string, persona?: MockPersona): string {
  const who = persona ?? role;
  const me: MeResponse & { environmentIds?: readonly string[] } = {
    subject: `mock-${who}`,
    email: `${who}@example.test`,
    role,
    organisationId,
    // Not part of /v1/me - carried in the mock "token" so the mock API can
    // apply ADR-0009's per-environment boundary without a Memberships table.
    ...(environmentIds ? { environmentIds } : {}),
    // ADR-0014: a federated user, as the pre-token trigger would have stamped them - with the name the provider mapped.
    ...(identityProvider ? { identityProvider, firstName: "Ada", lastName: "Lovelace", displayName: "Ada Lovelace" } : {}),
  };
  return `mock.${btoa(JSON.stringify(me))}`;
}

export function setMockSession(role: Role, organisationId?: string, environmentIds?: readonly string[], identityProvider?: string, persona?: MockPersona): void {
  window.localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(mockSessionFromToken(mockIdToken(role, organisationId, environmentIds, identityProvider, persona))));
}
